/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BenefitsService from 'src/services/benefits.service';
import { useFormik } from 'formik';

import { RegularButton, ICommonDropdownOption, CommonDropdown } from 'pg-ui-library';

import { BenefitSettings, ElectCoverageSettings, IBenefit } from 'src/interfaces';

import * as Yup from 'yup';

import { monetaryUSDFormat } from 'src/utils/currencyFormat';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import {
  ElectedCoverageEmptySettingsContainer,
  ElectedCoverageSettingsFormContainer,
  FormItem,
  InputContainer,
  StyledEOIWrap,
  SubmitContainer,
} from './styles';
import { useSmoker } from '../../enrollment.context';
import { BeneficiariesSettingsForm } from '../beneficiaries-settings-form';

const applyRateOverride = (rate: number, isSmoker: boolean, planDescription: string, benefitId: string): number => {
  //const isUniversalLifeSpouseByBenefitId = benefitId === 'UNLIES_NY' || benefitId === 'UNLIES_OT';
  //const isUniversalLifeSpouseByDescription =
  //  planDescription === 'Universal Life NY - Spouse' ||
  //  planDescription === 'Universal Life Non-NY - Spouse' ||
  //  (planDescription.includes('Universal Life') && planDescription.includes('Spouse'));

  // This block is intentionally left unfunctional.
  // const isUniversalLifeSpouse = isUniversalLifeSpouseByBenefitId || isUniversalLifeSpouseByDescription;
  // const isRateInRange = rate >= 8.6 && rate <= 27.5;
  // const isNonSmoker = !isSmoker;

  // This block is intentionally made unfunctional.
  // if (isUniversalLifeSpouse && isRateInRange && isNonSmoker) {
  //   return 8.67;
  // }

  return rate;
};

const ElectedCoverageSettingsFormWrapper: React.FC<{
  benefit: IBenefit;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (benefit: IBenefit) => void;
  // eslint-disable-next-line react/require-default-props
  onRequestBack?: () => void;
}> = ({ benefit, onSubmit, onRequestBack }) => {
  const [dropdownOptions, setDropdownOptions] = useState<ICommonDropdownOption[]>([]);
  const [hasDataConfig, setHasDataConfig] = useState<boolean>(
    (benefit.settings?.rates as ElectCoverageSettings)?.length > 0
  );
  const { cart, enrollmentType } = useTypedSelector(state => state.benefits.data);
  const smoker = useSmoker();
  const [warningAmount, setWarningAmount] = useState(false);

  const { t } = useTranslation();

  const enableVolLifeAdSettings =
    benefit.benefitId.includes('VOLADD_EE') ||
    benefit.benefitId.includes('VOLADD_CH') ||
    benefit.benefitId.includes('VOLADD_SP');

  const enableSTDSettings = benefit.benefitId.includes('STD_EXT');

  const schema = Yup.object({
    amount: Yup.number().min(0).required(),
    rate: Yup.number().nullable(),
    EOI: Yup.boolean().required(),
  });

  const initialValue = {
    amount: 0,
    rate: 0,
    EOI: false,
  };

  const initialIsValid = schema.isValidSync(initialValue);
  const formik = useFormik<{ rate: number; amount: number; EOI: boolean }>({
    initialValues: initialValue,
    isInitialValid: initialIsValid,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: values =>
      onSubmit({
        ...benefit,
        coverageAmount: Number(values.amount),
        price: values.rate || 0,
        settings: {
          ...(benefit.settings as BenefitSettings),
        },
      }),
  });

  const initBasedOnSTDSettings = () => {
    const dropdownOption: ICommonDropdownOption[] = [];
    const settings = benefit.settings?.rates as ElectCoverageSettings;

    const baseRate = settings[settings.length - 1].rate;
    const adjustedRate = applyRateOverride(baseRate, smoker, benefit.planDescription, benefit.benefitId);

    dropdownOption.push({
      key: settings[settings.length - 1].toString(),
      label: `${monetaryUSDFormat(settings[settings.length - 1].amount)} - Rate: ${monetaryUSDFormat(adjustedRate)} ${
        settings[settings.length - 1].EOI ? ' - EOI' : ''
      }`,
      value: settings[settings.length - 1].amount.toString(),
    });

    setDropdownOptions(dropdownOption);

    if (!settings[0]) {
      setHasDataConfig(false);
    } else {
      formik.setFieldValue('amount', settings[settings.length - 1].amount);
      formik.setFieldValue('EOI', settings[settings.length - 1].EOI);
      formik.setFieldValue('rate', adjustedRate);
    }
  };

  const initBasedOnVolLifeSettings = () => {
    const dropdownOption: ICommonDropdownOption[] = [];
    let settings = benefit.settings?.rates as ElectCoverageSettings;

    if (benefit.benefitId.includes('VOLADD_CH')) {
      const b = cart?.find(bft => bft.benefitId === 'LIFE_CH');

      if (b) {
        settings = settings.filter(item => item.amount === b.coverageAmount);
      }
    }
    if (benefit.benefitId.includes('VOLADD_EE')) {
      const b = cart?.find(bft => bft.benefitId === 'LIFE_EE');

      if (b) {
        settings = settings.filter(item => item.amount === b.coverageAmount);
      }
    }
    if (benefit.benefitId.includes('VOLADD_SP')) {
      const b = cart?.find(bft => bft.benefitId === 'LIFE_SP');
      if (b) {
        settings = settings.filter(item => item.amount === b.coverageAmount);
      }
    }

    settings.forEach((item, index) => {
      const adjustedRate = applyRateOverride(item.rate, smoker, benefit.planDescription, benefit.benefitId);
      dropdownOption.push({
        key: index.toString(),
        label: `${monetaryUSDFormat(item.amount)} - Rate: ${monetaryUSDFormat(adjustedRate)} ${
          item.EOI ? ' - EOI' : ''
        }`,
        value: item.amount.toString(),
      });
    });
    setDropdownOptions(dropdownOption);

    if (!settings[0]) {
      setHasDataConfig(false);
    } else {
      const baseRate = settings[0].rate;
      const adjustedRate = applyRateOverride(baseRate, smoker, benefit.planDescription, benefit.benefitId);
      formik.setFieldValue('amount', settings[0].amount);
      formik.setFieldValue('EOI', settings[0].EOI);
      formik.setFieldValue('rate', adjustedRate);
    }
  };

  const fetchAvailableBenefits = async (planDescription: string, type: string) => {
    try {
      const availableBenefits = await BenefitsService.getAvailableBenefits({
        planDescription,
        eventType: enrollmentType || '',
        type,
        smoker: smoker ? 'true' : 'false',
      });
      localStorage.setItem('availableBenefits', JSON.stringify(availableBenefits));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching available benefits:', error);
    }
  };

  const defaultInitCoverageConfig = () => {
    if (benefit.benefitId === 'LIFE_CH' && cart?.find(bft => bft.benefitId === 'VOLADD_CH')) {
      setWarningAmount(true);
      fetchAvailableBenefits('Voluntary AD&D Insurance-Child', 'Vol AD&D - Child');
    } else if (benefit.benefitId === 'LIFE_SP' && cart?.find(bft => bft.benefitId === 'VOLADD_SP')) {
      setWarningAmount(true);
      fetchAvailableBenefits('Voluntary AD&D Insurance-Spouse', 'Vol AD&D - Spouse');
    } else if (benefit.benefitId === 'LIFE_EE' && cart?.find(bft => bft.benefitId === 'VOLADD_EE')) {
      setWarningAmount(true);
      fetchAvailableBenefits('Voluntary AD&D Insurance-Employee', 'Vol AD&D - Employee');
    }

    if (hasDataConfig) {
      const dropdownOption: ICommonDropdownOption[] = [];
      let settings = benefit.settings?.rates as ElectCoverageSettings;

      if (
        benefit.benefitId === 'LIFE_SP' &&
        (benefit.type === 'Vol Life - Spouse' ||
          benefit.type === 'Voluntary Life - Spouse' ||
          benefit.planDescription?.includes('Vol Life') ||
          benefit.planDescription?.includes('Voluntary Life') ||
          benefit.planDescription?.includes('Spouse'))
      ) {
        const employeeBenefit = cart?.find(
          bft => bft.benefitId === 'LIFE_EE' && bft.coverageAmount && bft.settings?.rates
        );

        if (employeeBenefit && employeeBenefit.coverageAmount && employeeBenefit.settings?.rates) {
          const employeeSettings = employeeBenefit.settings.rates as ElectCoverageSettings;
          const employeeSelectedItem = employeeSettings.find(item => item.amount === employeeBenefit.coverageAmount);

          if (employeeSelectedItem) {
            const targetCoverage = employeeBenefit.coverageAmount * 0.5;
            const targetRate = employeeSelectedItem.rate * 0.5;

            const matchingOption = settings.find(
              item => item.amount === targetCoverage && Math.abs(item.rate - targetRate) < 0.01
            );

            if (matchingOption) {
              settings = [matchingOption];
            } else {
              const validOptions = settings.filter(item => item.amount <= targetCoverage);

              if (validOptions.length > 0) {
                const closestByCoverage = validOptions.reduce((prev, curr) => {
                  const prevDiff = Math.abs(prev.amount - targetCoverage);
                  const currDiff = Math.abs(curr.amount - targetCoverage);
                  return currDiff < prevDiff ? curr : prev;
                });

                if (closestByCoverage.amount === targetCoverage) {
                  const calculatedOption = {
                    amount: targetCoverage,
                    rate: targetRate,
                    EOI: closestByCoverage.EOI || false,
                    employerProvidedQuantity: (closestByCoverage as any).employerProvidedQuantity || 0,
                  };
                  settings = [calculatedOption];
                } else {
                  settings = settings.filter(item => item.amount === closestByCoverage.amount);

                  if (settings.length > 1) {
                    const closestByRate = settings.reduce((prev, curr) => {
                      const prevDiff = Math.abs(prev.rate - targetRate);
                      const currDiff = Math.abs(curr.rate - targetRate);
                      return currDiff < prevDiff ? curr : prev;
                    });
                    settings = [closestByRate];
                  }
                }
              } else {
                const calculatedOption = {
                  amount: targetCoverage,
                  rate: targetRate,
                  EOI: false,
                  employerProvidedQuantity: 0,
                };
                settings = [calculatedOption];
              }
            }
          }
        }
      }

      if (
        benefit.benefitId === 'LIFE_CH' &&
        (benefit.type === 'Vol Life - Child' ||
          benefit.type === 'Voluntary Life - Child' ||
          benefit.planDescription?.includes('Vol Life') ||
          benefit.planDescription?.includes('Voluntary Life') ||
          benefit.planDescription?.includes('Child'))
      ) {
        const employeeBenefit = cart?.find(
          bft => bft.benefitId === 'LIFE_EE' && bft.coverageAmount && bft.settings?.rates
        );

        if (employeeBenefit && employeeBenefit.coverageAmount && employeeBenefit.settings?.rates) {
          const employeeSettings = employeeBenefit.settings.rates as ElectCoverageSettings;
          const employeeSelectedItem = employeeSettings.find(item => item.amount === employeeBenefit.coverageAmount);

          if (employeeSelectedItem) {
            const targetCoverage = employeeBenefit.coverageAmount * 0.1;
            const targetRate = employeeSelectedItem.rate * 0.1;

            const matchingOption = settings.find(
              item => item.amount === targetCoverage && Math.abs(item.rate - targetRate) < 0.01
            );

            if (matchingOption) {
              settings = [matchingOption];
            } else {
              const validOptions = settings.filter(item => item.amount <= targetCoverage);

              if (validOptions.length > 0) {
                const closestByCoverage = validOptions.reduce((prev, curr) => {
                  const prevDiff = Math.abs(prev.amount - targetCoverage);
                  const currDiff = Math.abs(curr.amount - targetCoverage);
                  return currDiff < prevDiff ? curr : prev;
                });

                if (closestByCoverage.amount === targetCoverage) {
                  const calculatedOption = {
                    amount: targetCoverage,
                    rate: targetRate,
                    EOI: closestByCoverage.EOI || false,
                    employerProvidedQuantity: (closestByCoverage as any).employerProvidedQuantity || 0,
                  };
                  settings = [calculatedOption];
                } else {
                  settings = settings.filter(item => item.amount === closestByCoverage.amount);

                  if (settings.length > 1) {
                    const closestByRate = settings.reduce((prev, curr) => {
                      const prevDiff = Math.abs(prev.rate - targetRate);
                      const currDiff = Math.abs(curr.rate - targetRate);
                      return currDiff < prevDiff ? curr : prev;
                    });
                    settings = [closestByRate];
                  }
                }
              } else {
                const calculatedOption = {
                  amount: targetCoverage,
                  rate: targetRate,
                  EOI: false,
                  employerProvidedQuantity: 0,
                };
                settings = [calculatedOption];
              }
            }
          }
        }
      }

      settings.forEach((item, index) => {
        const adjustedRate = applyRateOverride(item.rate, smoker, benefit.planDescription, benefit.benefitId);
        dropdownOption.push({
          key: index.toString(),
          label: `${monetaryUSDFormat(item.amount)} - Rate: ${monetaryUSDFormat(adjustedRate)} ${
            item.EOI ? ' - EOI' : ''
          }`,
          value: item.amount.toString(),
        });
      });
      setDropdownOptions(dropdownOption);

      const baseRate = settings[0].rate;
      const adjustedRate = applyRateOverride(baseRate, smoker, benefit.planDescription, benefit.benefitId);
      formik.setFieldValue('amount', settings[0].amount);
      formik.setFieldValue('EOI', settings[0].EOI);
      formik.setFieldValue('rate', adjustedRate);
    }
  };

  useEffect(() => {
    if (!hasDataConfig) {
      return;
    }
    if (enableVolLifeAdSettings) {
      initBasedOnVolLifeSettings();
    } else if (enableSTDSettings) {
      initBasedOnSTDSettings();
    } else {
      defaultInitCoverageConfig();
    }
  }, []);

  if (!hasDataConfig) {
    return (
      <ElectedCoverageEmptySettingsContainer>
        {t('benefits.coverageSettings.emptyConfig')}
      </ElectedCoverageEmptySettingsContainer>
    );
  }

  return (
    <ElectedCoverageSettingsFormContainer>
      <FormItem>
        <h4>{t('benefits.coverageSettings.title')}</h4>
      </FormItem>
      <FormItem>
        <InputContainer>
          <CommonDropdown
            label=""
            id="distribution_method"
            name="distribution_method"
            value={formik.values.amount}
            options={dropdownOptions}
            wrapperStyle={{ width: '100%' }}
            setValue={(value: string) => {
              formik.setFieldValue('amount', value);
              const item = (benefit.settings?.rates as ElectCoverageSettings).find(v => v.amount.toString() === value);
              formik.setFieldValue('EOI', item?.EOI);
              const baseRate = item?.rate || 0;
              const adjustedRate = applyRateOverride(baseRate, smoker, benefit.planDescription, benefit.benefitId);
              formik.setFieldValue('rate', adjustedRate);
            }}
            selectStyle={{
              minWidth: '55%',
              border: '0.3px rgb(100,100,100) solid',
            }}
          />
        </InputContainer>
        <StyledEOIWrap>{formik.values.EOI ? t('benefits.coverageSettings.eoi') : ''}</StyledEOIWrap>
      </FormItem>
      {warningAmount ? <p style={{ color: 'red' }}>{t('benefits.benefit.Warning')}</p> : ''}
      <SubmitContainer>
        {onRequestBack && <RegularButton onClick={onRequestBack}>{t('global.button.back')}</RegularButton>}
        <RegularButton
          disabled={!formik.isValid}
          style={{ margin: '0 3px 0 0' }}
          type="submit"
          onClick={() => formik.handleSubmit()}
        >
          {t('global.form.submit')}
        </RegularButton>
      </SubmitContainer>
    </ElectedCoverageSettingsFormContainer>
  );
};

// eslint-disable-next-line no-unused-vars
export const ElectedCoverageSettingsForm: React.FC<{
  benefit: IBenefit;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (benefit: IBenefit) => void;
  // eslint-disable-next-line react/require-default-props
  onRequestBack?: () => void;
}> = ({
  benefit,
  // eslint-disable-next-line no-unused-vars
  onSubmit,
  onRequestBack,
}) => {
  const [openBeneficiaries, setOpenBeneficiaries] = useState(false);
  const [modifiedBenefit, setModifiedBenefit] = useState(benefit);

  const submitWrapper = (b: IBenefit) => {
    if (benefit.necessaryToProvideBeneficiariesInformation) {
      setOpenBeneficiaries(true);
      setModifiedBenefit(b);
    } else {
      onSubmit(b);
    }
  };

  if (benefit.necessaryToProvideBeneficiariesInformation && openBeneficiaries) {
    return <BeneficiariesSettingsForm benefit={modifiedBenefit} onSubmit={onSubmit} onRequestBack={onRequestBack} />;
  }

  return (
    <ElectedCoverageSettingsFormWrapper benefit={benefit} onSubmit={submitWrapper} onRequestBack={onRequestBack} />
  );
};
